import { IncludeExcludeModel } from '../../../Common/Misc/models/include-exclude-model';
import { CirculationType } from '../../../Discover/Targeting/models/circulation.model';
import { ContactModel } from '../../../Common/TargetingShared/model/contact.model';
import { JournodexGeographyModel } from '../../../Common/Journodex/models/journodex-geography.model';
import { JournodexSubjectParentModel } from '../../../Common/Journodex/models/journodex.subject.model';
import { JobRoleModel } from '../../Core/models/job-role.model';
import type { LanguageModel } from '../../../Core/Localization/models/language.model';
import { OutletModel } from './outlet.model';
import { MediaMarket } from '../../Core/models/media-market.model';
import { TargetingSearch } from './targeting-search.model';
import { SubjectUtils } from '../../../Common/Journodex/services/journodex-subjects.service';
import { IdNamePairModel } from '../../../Common/Misc/models/id-name-pair.model';
import { TargetingMediaTypeEnum } from '../enums/targeting-media-type.enum';


export class TargetingSearchFullModel {
    name: string;
    mediaTypes: IncludeExcludeModel<TargetingMediaTypeEnum>;
    productTypes: IncludeExcludeModel<string>;
    marketSectors: IncludeExcludeModel<string>;
    coverageFocuses: IncludeExcludeModel<string>;
    productFrequencies: IncludeExcludeModel<string>;
    targetAudiences: IncludeExcludeModel<string>;

    audienceReachType: CirculationType;
    audienceReachMin: number;
    audienceReachMax: number;
    includePrivateContacts: boolean;
    groupByOutlet: boolean;

    contacts: ContactModel[];
    keywords: IncludeExcludeModel<string>;
    subjects: IncludeExcludeModel<JournodexSubjectParentModel>;
    geographies: IncludeExcludeModel<JournodexGeographyModel>;
    jobTitles: IncludeExcludeModel<string>;
    jobRoles: IncludeExcludeModel<JobRoleModel>;
    languages: IncludeExcludeModel<LanguageModel>;

    outlets: OutletModel[];
    outletKeywords: IncludeExcludeModel<string>;
    outletSubjects: IncludeExcludeModel<JournodexSubjectParentModel>;
    areasOfCoverage: IncludeExcludeModel<JournodexGeographyModel>;
    mediaMarkets: IncludeExcludeModel<MediaMarket>;

    constructor(search?: TargetingSearchFullModel) {
        if (search) {
            this.name = search.name ?? null;
            this.mediaTypes = search.mediaTypes;
            this.productTypes = search.productTypes;
            this.marketSectors = search.marketSectors;
            this.coverageFocuses = search.coverageFocuses;
            this.productFrequencies = search.productFrequencies;
            this.targetAudiences = search.targetAudiences;
            this.audienceReachType = search?.audienceReachType ?? null;
            this.audienceReachMin = search.audienceReachMin ?? null;
            this.audienceReachMax = search.audienceReachMax ?? null;
            this.includePrivateContacts = search.includePrivateContacts;

            this.contacts = search.contacts;
            this.keywords = search.keywords;
            this.subjects = search.subjects;
            this.geographies = search.geographies;
            this.jobTitles = search.jobTitles;
            this.jobRoles = search.jobRoles;
            this.languages = search.languages;

            this.outlets = search.outlets;
            this.outletKeywords = search.outletKeywords;
            this.outletSubjects = search.outletSubjects;
            this.areasOfCoverage = search.areasOfCoverage;
            this.mediaMarkets = search.mediaMarkets;
        }
    }

    merge(other: Partial<TargetingSearchFullModel>) {
        if (other.name !== undefined) {
            this.name = other.name;
        }
        if (other.mediaTypes !== undefined) {
            this.mediaTypes = other.mediaTypes;
        }
        if (other.productTypes !== undefined) {
            this.productTypes = other.productTypes;
        }
        if (other.marketSectors !== undefined) {
            this.marketSectors = other.marketSectors;
        }
        if (other.coverageFocuses !== undefined) {
            this.coverageFocuses = other.coverageFocuses;
        }
        if (other.productFrequencies !== undefined) {
            this.productFrequencies = other.productFrequencies;
        }
        if (other.targetAudiences !== undefined) {
            this.targetAudiences = other.targetAudiences;
        }
        if (other.audienceReachType !== undefined) {
            this.audienceReachType = other.audienceReachType;
        }
        if (other.audienceReachMin !== undefined) {
            this.audienceReachMin = other.audienceReachMin;
        }
        if (other.audienceReachMax !== undefined) {
            this.audienceReachMax = other.audienceReachMax;
        }
        if (other.includePrivateContacts !== undefined) {
            this.includePrivateContacts = other.includePrivateContacts;
        }
        if (other.contacts !== undefined) {
            this.contacts = other.contacts;
        }
        if (other.keywords !== undefined) {
            this.keywords = other.keywords;
        }
        if (other.subjects !== undefined) {
            this.subjects = other.subjects;
        }
        if (other.geographies !== undefined) {
            this.geographies = other.geographies;
        }
        if (other.jobTitles !== undefined) {
            this.jobTitles = other.jobTitles;
        }
        if (other.jobRoles !== undefined) {
            this.jobRoles = other.jobRoles;
        }
        if (other.languages !== undefined) {
            this.languages = other.languages;
        }
        if (other.outlets !== undefined) {
            this.outlets = other.outlets;
        }
        if (other.outletKeywords !== undefined) {
            this.outletKeywords = other.outletKeywords;
        }
        if (other.outletSubjects !== undefined) {
            this.outletSubjects = other.outletSubjects;
        }
        if (other.areasOfCoverage !== undefined) {
            this.areasOfCoverage = other.areasOfCoverage;
        }
        if (other.mediaMarkets !== undefined) {
            this.mediaMarkets = other.mediaMarkets;
        }
        // when we change the targeting form, sometimes groupByOutlet does not get passed...
        // we don't want to set it to false in that scenario
        if (other.hasOwnProperty('groupByOutlet')) {
            if (other.groupByOutlet) {
                this.groupByOutlet = other.groupByOutlet;
            } else {
                this.groupByOutlet = false;
            }
        }
    }

    toBaseSearch(): Partial<TargetingSearch> {
        return({
            name: this.name,
            mediaTypes: this.mediaTypes,
            productTypes: this.productTypes,
            marketSectors: this.marketSectors,
            coverageFocuses: this.coverageFocuses,
            productFrequencies: this.productFrequencies,
            targetAudiences: this.targetAudiences,
            audienceReachFilter: {
                type: this.audienceReachType,
                range: {
                    min: this.audienceReachMin ?? null,
                    max: this.audienceReachMax ?? null,
                }
            },
            includePrivateContacts: this.includePrivateContacts,

            contactIdsNames: this.convertToIdPair(this.contacts),
            monitoringKeywords: this.keywords,
            subjects: this.mapSubjectIncludeExclude(this.subjects, 'id'),
            geographies: this.mapIncludeExclude(this.geographies, 'collectionId'),
            jobTitles: this.jobTitles,
            jobRoles: this.mapIncludeExclude(this.jobRoles, 'id'),
            languages: this.mapIncludeExclude<string>(this.languages, 'id'),
            outletIdsNames: this.convertToIdPair(this.outlets),
            outletMonitoringKeywords: this.outletKeywords,
            outletSubjects: this.mapSubjectIncludeExclude(this.outletSubjects, 'id'),
            areasOfCoverage: this.mapIncludeExclude(this.areasOfCoverage, 'collectionId'),
            mediaMarkets: this.mapIncludeExclude(this.mediaMarkets, 'id'),
            groupByOutlet: this.groupByOutlet
        });
    }

    private convertToIdPair(data: OutletModel[] | ContactModel[]): IncludeExcludeModel<IdNamePairModel> {
        return new IncludeExcludeModel<IdNamePairModel>({
            include: data?.map(x => new IdNamePairModel(x.name, x.id)) ?? [],
            exclude: [],
            includeAll: []
        });

    }

    private mapIncludeExclude<T = number>(value: IncludeExcludeModel<any>, mapKey: string): IncludeExcludeModel<T> {
        return new IncludeExcludeModel<T>({
            include: value?.include?.map(x => x[mapKey]) ?? [],
            exclude: value?.exclude?.map(x => x[mapKey]) ?? [],
            includeAll: value?.includeAll?.map(x => x[mapKey]) ?? [],
        });
    }

    private mapSubjectIncludeExclude<T = number>(value: IncludeExcludeModel<JournodexSubjectParentModel>, mapKey: string): IncludeExcludeModel<T> {
        return new IncludeExcludeModel<T>({
            include: SubjectUtils.getOnlyChildrenOfSubjects(value?.include)?.map(x => x[mapKey]) ?? [],
            exclude: SubjectUtils.getOnlyChildrenOfSubjects(value?.exclude)?.map(x => x[mapKey]) ?? [],
            includeAll: SubjectUtils.getOnlyChildrenOfSubjects(value?.includeAll)?.map(x => x[mapKey]) ?? [],
        });
    }
}
